import './StatisticsPro.sass';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Header, ScrollView } from '$uikit';
import { Loader, MetaTags } from '$shared/components';
import { IcLink28, IcUsers28 } from '$assets';
import { CurrencyEnum } from '$store/models';
import { toastActions } from '$store/toast';
import { useTranslator } from '$hooks';
import { goTo } from '$navigation/helper';
import { STATISTICS_PRO_FANS, STATISTICS_PRO_LINKS } from '$shared/constants/pages';

import {
  EarningsCategoryMeta,
  getAvailableCategoriesByCurrency,
  IncomeType,
  StatisticsProDataset,
} from './StatisticsPro.types';
import { fetchStatisticsPro } from './StatisticsPro.api';
import { DAY_SECONDS, presetToRange } from './utils/dateRange';
import { DateRangePreset } from './components/DateRangeSection';
import { CategoryFilter } from './components/CategoryFilter';
import { CurrencyButton } from './components/CurrencyButton';
import { DateButton } from './components/DateButton';
import { StatsChart } from './components/StatsChart';
import { StatsTable } from './components/StatsTable';
import { downloadCsv } from './utils/csvExport';

const EMPTY_DATASET: StatisticsProDataset = { rows: [], days: [] };
const EMPTY_CATEGORIES: Record<CurrencyEnum, EarningsCategoryMeta[]> = {
  [CurrencyEnum.EUR]: [],
  [CurrencyEnum.USD]: [],
  [CurrencyEnum.TON]: [],
  [CurrencyEnum.PLACE]: [],
};

const pinnedTypesOf = (categories: EarningsCategoryMeta[]): IncomeType[] =>
  categories.filter((c) => c.pinned).map((c) => c.type);

export const StatisticsPro = () => {
  const dispatch = useDispatch();
  const t = useTranslator();

  const [currency, setCurrency] = useState<CurrencyEnum>(CurrencyEnum.EUR);
  const [preset, setPreset] = useState<DateRangePreset>('30d');
  const [range, setRange] = useState<[number, number]>(() => presetToRange('30d', 0, 0));

  const [dataset, setDataset] = useState<StatisticsProDataset>(EMPTY_DATASET);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedOnce, setLoadedOnce] = useState(false);

  const [selectedByCurrency, setSelectedByCurrency] = useState<
    Record<CurrencyEnum, IncomeType[]>
  >({
    [CurrencyEnum.EUR]: [],
    [CurrencyEnum.USD]: [],
    [CurrencyEnum.TON]: [],
    [CurrencyEnum.PLACE]: [],
  });
  const defaultedRef = useRef<Record<CurrencyEnum, Set<IncomeType>>>({
    [CurrencyEnum.EUR]: new Set(),
    [CurrencyEnum.USD]: new Set(),
    [CurrencyEnum.TON]: new Set(),
    [CurrencyEnum.PLACE]: new Set(),
  });

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchStatisticsPro({ from: range[0], to: range[1] })
      .then((data) => {
        if (cancelled) return;
        setDataset(data);
        setLoadedOnce(true);
      })
      .catch((e) => {
        if (cancelled) return;
        dispatch(toastActions.setToastFail(e?.message || 'Failed to load statistics'));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range, dispatch]);

  const availableByCurrency = useMemo(
    () =>
      dataset.rows.length
        ? getAvailableCategoriesByCurrency(dataset.rows)
        : EMPTY_CATEGORIES,
    [dataset],
  );

  useEffect(() => {
    setSelectedByCurrency((prev) => {
      let changed = false;
      const next = { ...prev };
      (Object.keys(availableByCurrency) as CurrencyEnum[]).forEach((ccy) => {
        const defaulted = defaultedRef.current[ccy];
        const additions: IncomeType[] = [];
        for (const cat of availableByCurrency[ccy]) {
          if (cat.pinned && !defaulted.has(cat.type)) {
            defaulted.add(cat.type);
            if (!next[ccy].includes(cat.type)) additions.push(cat.type);
          }
        }
        if (additions.length) {
          next[ccy] = [...next[ccy], ...additions];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [availableByCurrency]);

  const labels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const list of Object.values(availableByCurrency)) {
      for (const cat of list) map[cat.type] = cat.label;
    }
    return map;
  }, [availableByCurrency]);

  const categories = availableByCurrency[currency];
  const selected = selectedByCurrency[currency];

  const handlePresetChange = useCallback(
    (next: DateRangePreset, startDay: number, endDay: number) => {
      setPreset(next);
      setRange(presetToRange(next, startDay, endDay));
    },
    [],
  );

  const toggleCategory = useCallback(
    (type: IncomeType) => {
      setSelectedByCurrency((prev) => {
        const list = prev[currency];
        const next = list.includes(type)
          ? list.filter((t) => t !== type)
          : [...list, type];
        return { ...prev, [currency]: next };
      });
    },
    [currency],
  );

  const resetSelectedCategories = useCallback(() => {
    setSelectedByCurrency((prev) => ({
      ...prev,
      [currency]: pinnedTypesOf(availableByCurrency[currency]),
    }));
  }, [currency, availableByCurrency]);

  const { days, values } = useMemo(() => {
    const [from, to] = range;
    const weekly = (to - from) / DAY_SECONDS > 90;
    const WEEK = 7 * DAY_SECONDS;

    const bucketOf = (day: number): number => {
      if (!weekly) return day;
      const dse = Math.floor(day / DAY_SECONDS);
      const offset = ((dse % 7) + 3) % 7;
      return (dse - offset) * DAY_SECONDS;
    };

    const buckets: number[] = [];
    const step = weekly ? WEEK : DAY_SECONDS;
    for (let b = bucketOf(from); b <= to; b += step) {
      buckets.push(b);
    }

    const map: Record<number, Partial<Record<IncomeType, number>>> = {};
    for (const b of buckets) {
      map[b] = {};
    }
    for (const row of dataset.rows) {
      if (row.currency !== currency) continue;
      if (row.date < from || row.date > to) continue;
      const target = map[bucketOf(row.date)];
      if (!target) continue;
      for (const [type, amount] of Object.entries(row.earnings)) {
        target[type] = (target[type] ?? 0) + amount;
      }
    }

    return { days: buckets, values: map };
  }, [range, currency, dataset]);

  const { tableDays, tableValues } = useMemo(() => {
    const [from, to] = range;
    const buckets: number[] = [];
    for (let d = from; d <= to; d += DAY_SECONDS) {
      buckets.push(d);
    }

    const map: Record<number, Partial<Record<IncomeType, number>>> = {};
    for (const b of buckets) {
      map[b] = {};
    }
    for (const row of dataset.rows) {
      if (row.currency !== currency) continue;
      const target = map[row.date];
      if (!target) continue;
      for (const [type, amount] of Object.entries(row.earnings)) {
        target[type] = (target[type] ?? 0) + amount;
      }
    }

    return { tableDays: buckets, tableValues: map };
  }, [range, currency, dataset]);

  const othersTypes = useMemo(
    () => categories.filter((c) => !selected.includes(c.type)).map((c) => c.type),
    [categories, selected],
  );

  const handleExport = useCallback(() => {
    downloadCsv({
      rows: tableDays.map((d) => ({ date: d, values: tableValues[d] ?? {} })),
      categories: selected,
      othersTypes,
      labels,
      filename: `statistics-pro-${currency}-${tableDays[0] ?? 'empty'}-${
        tableDays[tableDays.length - 1] ?? 'empty'
      }.csv`,
    });
  }, [tableDays, tableValues, selected, othersTypes, labels, currency]);

  return (
    <>
      <MetaTags title={t('statistics_pro_title')} />
      <Header
        showBackButton
        rightIcons={[
          {
            icon: <IcLink28 />,
            onClick: () => goTo(STATISTICS_PRO_LINKS),
          },
          {
            icon: <IcUsers28 />,
            onClick: () => goTo(STATISTICS_PRO_FANS),
          },
        ]}
      >
        {t('statistics_pro_title')}
      </Header>
      <ScrollView className="StatisticsPro">
        <div className="StatisticsProControls">
          <CurrencyButton value={currency} onChange={setCurrency} />
          <DateButton
            preset={preset}
            startDay={range[0]}
            endDay={range[1]}
            onChange={handlePresetChange}
          />
        </div>

        {isLoading && !loadedOnce ? (
          <Loader isWrapped />
        ) : (
          <>
            <StatsChart
              days={days}
              values={values}
              categories={categories}
              selected={selected}
              labels={labels}
              currency={currency}
            />

            <CategoryFilter
              categories={categories}
              selected={selected}
              onToggle={toggleCategory}
              onReset={resetSelectedCategories}
            />

            <StatsTable
              days={tableDays}
              values={tableValues}
              selected={selected}
              othersTypes={othersTypes}
              labels={labels}
              currency={currency}
              onExport={handleExport}
            />
          </>
        )}
      </ScrollView>
    </>
  );
};
